import { ContentType as ForgeContentType } from "@amzn/ads-forge-client";
import type {
    GeneratedInsight,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { startOfHour } from "date-fns";
import { isString } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { SUMMARY_ASSET_ERROR_MESSAGE } from "../../constants/deckDecoration";
import { G4sPlacement } from "../../constants/g4sPlacement";
import { useSetInsightDetailComponents } from "../../hooks/insightDetail/useSetInsightDetailComponents";
import { insightDetailComponentStore } from "../../store/insightDetailComponent";
import type { CardDetail } from "../../types/card";
import { InsightPreviewFileFormats } from "../../types/insightPreviewFileFormats";
import type {
    InsightDetailOption,
    InsightDetailSelectionProps,
} from "../../utils/insightDetail.utils";
import { InsightDetailType } from "../../utils/insightDetail.utils";
import {
    BodyComponentBuilder,
    FooterComponentBuilder,
    Header,
    HeaderCardDetails,
    HeaderComponentBuilder,
    HeaderTitle,
} from "../common/insightDetail.components";
import { ToolbarButtonGroup } from "../common/styled.components";
import { DeckDecorationWithLanguageModalButton } from "../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import { EditInputsButton } from "../discovery/insights/generatedInsights/PackagedInsightCard/PackagedInsightsDetails.Actions";
import {
    ForgeNarrativeDownloadButton,
    ForgeNarrativeSelector,
    ForgeNarrativeTranslateModalButton,
    getForgeApiMFCClient,
} from "../remote";

export interface ForgeMFCPackagedInsightDetails {
    details: CardDetail[];
    title: string;
    identifier: string;
    insightType: InsightType;
    downloadLoading: boolean;
    insight: GeneratedInsight;
    reset: () => void;
}

export const useSetForgeMFCPackagedInsightDetails = (
    props: ForgeMFCPackagedInsightDetails,
): (() => void) => {
    const {
        identifier,
        insightType,
        insight,
        details,
        title,
        downloadLoading,
        reset,
    } = props;

    const [resetStore] = useStore(insightDetailComponentStore, (state) => [
        state.reset,
    ]);
    const [areComponentsReady, setComponentsReady] = useState(false);

    const [numPages, setNumPages] = useState<number>(0);

    const handleClose = () => {
        setNumPages(0);
        setComponentsReady(false);
        resetStore();
        reset();
    };

    const PackagedInsightHeaderOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightHeader: React.FunctionComponent<
            InsightDetailSelectionProps
        > = () => (
            <HeaderComponentBuilder
                loading={downloadLoading}
                headerComponents={[
                    <Header
                        numPages={numPages}
                        identifier={identifier}
                        insightType={insightType}
                    />,
                    <HeaderTitle title={title} />,
                    <HeaderCardDetails
                        details={details}
                        numPages={numPages}
                        previewFileFormat={InsightPreviewFileFormats.FORGE}
                        identifier={identifier}
                        insightType={insightType}
                        aiSummaryOverview={insight.aiSummaryOverview}
                        aiSummaryExecutive={insight.aiSummaryExecutive}
                        buttonOverride={[
                            <ForgeNarrativeTranslateModalButton
                                contentIdentifier={identifier}
                            />,
                            <DeckDecorationWithLanguageModalButton
                                identifier={identifier}
                                insightType={insightType}
                                documentUrlAsync={getDocumentUrlAsync}
                                placement={G4sPlacement.SIDE_PANEL}
                            />,
                        ]}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightHeader,
        };
    }, [identifier, numPages, details, downloadLoading]);

    const PackagedInsightBodyOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightBody: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <BodyComponentBuilder
                loading={false}
                bodyComponents={[
                    <ForgeNarrativeSelector
                        selectedSlides={new Set()}
                        setSelectedSlides={() => {}}
                        contentIdentifier={identifier}
                        contentType={ForgeContentType.PACKAGED}
                        setNumPages={setNumPages}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightBody,
        };
    }, [identifier, areComponentsReady]);

    const getDocumentUrlAsync = useCallback(async () => {
        // get clients
        const [forgeClient, blinkClient] = await Promise.all([
            getForgeApiMFCClient(),
            getBlinkApiClientV2(),
        ]);

        const cacheId = startOfHour(new Date()).toISOString();
        const resourceIdentifier = `${identifier}_${cacheId}`;

        try {
            // build forge pptx
            const forgePptxResponse = await forgeClient.getNarrativeAsPptx({
                contentIdentifier: identifier,
                contentType: ForgeContentType.PACKAGED,
            });

            // convert pptx -> pdf
            const forgePdfConversionResponse =
                await blinkClient.createAsyncOfficeConvert({
                    clientId: "IHUB",
                    applicationContext: "forge-summary",
                    format: "pdf",
                    resourceIdentifier,
                    inputLocation: forgePptxResponse.s3Location,
                    outputLocation: {
                        bucket: forgePptxResponse.s3Location?.bucket,
                        key: `${resourceIdentifier}.pdf`,
                    },
                });

            // wait for pdf to finish
            const { outputFileDownloadUrl } =
                await blinkClient.pollGetAsyncOfficeConvert({
                    executionId: forgePdfConversionResponse.execution,
                });

            if (isString(outputFileDownloadUrl)) return outputFileDownloadUrl;

            throw new Error(SUMMARY_ASSET_ERROR_MESSAGE);
        } catch (err) {
            console.error("Failed to create asset used for summary:", err);
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            throw new Error(`${SUMMARY_ASSET_ERROR_MESSAGE}: ${errorMessage}`);
        }
    }, [identifier]);

    const PackagedInsightFooterOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightFooter: React.FunctionComponent<
            InsightDetailSelectionProps
        > = () => (
            <FooterComponentBuilder
                loading={false}
                handleClose={handleClose}
                identifier={identifier}
                insightType={insightType}
                footerComponents={[
                    <ToolbarButtonGroup>
                        <ForgeNarrativeDownloadButton
                            contentIdentifier={identifier}
                            contentType={ForgeContentType.PACKAGED}
                        />
                        <EditInputsButton insight={insight} />
                    </ToolbarButtonGroup>,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightFooter,
        };
    }, [identifier, numPages, downloadLoading]);

    const setInsightDetailComponents = useSetInsightDetailComponents({
        identifier,
        familyName: "",
        InsightHeaderOption: PackagedInsightHeaderOption,
        InsightBodyOption: PackagedInsightBodyOption,
        InsightFooterOption: PackagedInsightFooterOption,
        headerReady: areComponentsReady,
        bodyReady: areComponentsReady,
        footerReady: areComponentsReady,
        handleMount: () => {},
        handleClose,
    });

    return () => {
        setComponentsReady(true);
        setInsightDetailComponents();
    };
};
